import { useResetRecommendedBMCRPQueries } from "hooks/useResetRecommendedBMCRPQueries";
import { useUpdateBuildingModelMutation } from "mutations/useUpdateBuildingModelMutation";
import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import useBuildingLoadingState from "recoilStore/useBuildingLoadingState";

export const useQueryOnStatusUpdate = (buildingModelUid: string) => {
    const queryClient = useQueryClient();
    const { setLoadingState } = useBuildingLoadingState(buildingModelUid);
    const resetRecommendedBMCRPQueries = useResetRecommendedBMCRPQueries(buildingModelUid);

    const reset = () => {
        queryClient.resetQueries({
            queryKey: [buildingModelUid, "buildingModelReport"],
        });

        queryClient.invalidateQueries({
            queryKey: ["buildingModelsTable"],
        });
        queryClient.resetQueries({
            queryKey: ["buildingModelConstruction", buildingModelUid],
        });
        setLoadingState((prev) => ({
            ...prev,
            constructionSidebarLoading: false,
            buildingReportsLoading: false,
        }));

        queryClient.invalidateQueries({
            queryKey: ["buildingModelHeader", buildingModelUid],
        });

        resetRecommendedBMCRPQueries();
    };

    return { onSuccess: reset, onError: reset };
};

/*
 * TODO: This method really shouldn't be necessary and it adds unnecessary overhead whenever adding new attributes.
 * We really shouldn't have two different models (BuildingModelConstruction/BuildingProperties) at all.  We should
 * just have one class, and then copying data across becomes unnecessary.
 */
export const buildBuildingPropertiesMap = (building: BuildingModelConstruction): BuildingProperties => ({
    buildingUid: building.buildingUid,
    availableRoofAreaRatio: building.availableRoofAreaRatio ?? undefined,
    ledInstalledRatio: building.ledInstalledRatio ?? undefined,
    yearBuiltOriginal: building.yearBuiltOriginal ?? undefined,
    buildingArchetype: building.buildingArchetype ?? undefined,
    buildingHeight: building.buildingHeight ?? undefined,
    floorsAboveGrade: building.floorsAboveGrade ?? undefined,
    grossFloorArea: building.grossFloorArea ?? undefined,
    coolingMultiplier: building.coolingMultiplier ?? undefined,
    heatingMultiplier: building.heatingMultiplier ?? undefined,
    lightingMultiplier: building.lightingMultiplier ?? undefined,
    plugLoadMultiplier: building.plugLoadMultiplier ?? undefined,
    processMultiplier: building.processMultiplier ?? undefined,
    domesticHotWaterMultiplier: building.domesticHotWaterMultiplier ?? undefined,
    ventilationRateMultiplier: building.ventilationRateMultiplier ?? undefined,
    fansMultiplier: building.fansMultiplier ?? undefined,
    pumpsMultiplier: building.pumpsMultiplier ?? undefined,
    defaultDiscountRate: building.defaultDiscountRate ?? undefined,
    defaultGridIntensityScheduleScenario: building.defaultGridIntensityScheduleScenario ?? undefined,
    defaultWeightEmissionSavings: building.defaultWeightEmissionSavings ?? undefined,
    defaultWeightNetPresentValue: building.defaultWeightNetPresentValue ?? undefined,
    defaultUtilityInflationRate: building.defaultUtilityInflationRate ?? undefined,
    defaultElectricityUtilityRate: building.defaultElectricityUtilityRate ?? undefined,
    defaultNaturalGasUtilityRate: building.defaultNaturalGasUtilityRate ?? undefined,
    defaultSteamUtilityRate: building.defaultSteamUtilityRate ?? undefined,
    assumedHeatingEnergyType: building.assumedHeatingEnergyType ?? undefined,
    assumedCoolingEnergyType: building.assumedCoolingEnergyType ?? undefined,
    assumedDhwEnergyType: building.assumedDhwEnergyType ?? undefined,
    defaultLandlordShareCost: building.defaultLandlordShareCost ?? undefined,
    defaultLandlordShareElectricity: building.defaultLandlordShareElectricity ?? undefined,
    defaultLandlordShareNaturalGas: building.defaultLandlordShareNaturalGas ?? undefined,
    defaultLandlordShareSteam: building.defaultLandlordShareSteam ?? undefined,
    assumedGrossAssetValue: building.assumedGrossAssetValue ?? undefined,
    customerBuildingIdentifier: building.customerBuildingIdentifier ?? undefined,
    notes: building.notes ?? undefined,
    leasingStructure: building.leasingStructure ?? undefined,
    assumedExitCapRate: building.assumedExitCapRate ?? undefined,
    assumedExitYear: building.assumedExitYear ?? undefined,
});

export const valueIsValid = (value: string | number | null | undefined) => value !== undefined && value !== null;

export const getValueForKey = (key: string, construction: BuildingModelConstruction) =>
    key in construction.location
        ? construction.location[key as keyof Location]
        : construction[key as keyof BuildingModelConstruction];

export const useSelectedBuildingPolygon = (buildingConstruction: BuildingModelConstruction | undefined) => {
    const [selectedFootprintPolygon, setSelectedFootprintPolygon] = useState<FootprintPolygon>();

    useEffect(() => {
        setSelectedFootprintPolygon(buildingConstruction?.buildingFootprint);
    }, [buildingConstruction]);

    return { selectedFootprintPolygon, setSelectedFootprintPolygon };
};

export const useUpdateBuildingModel = (buildingModelUid: string, buildingAddress?: Address | undefined) => {
    const { onSuccess, onError } = useQueryOnStatusUpdate(buildingModelUid);

    const { mutate: updateBuildingModel, error } = useUpdateBuildingModelMutation(
        buildingModelUid,
        onSuccess,
        onError,
        buildingAddress
    );

    return { updateBuildingModel, error };
};
